import type {
	SketchEntityReferenceControllerOptions,
	SketchEntityReferencePreviewSession,
	SketchEntityReferenceSelectionResolveInput,
	SketchEntityReferenceAllInnerLoopsResolveInput,
} from '../../../types/reference/sketch-entity-reference/SketchEntityReferenceController';
import type {
	SketchEntityReferenceRemoveRelationInput,
	SketchEntityReferenceRemoveRelationResult,
} from '../../../types/reference/sketch-entity-reference/SketchEntityReferenceLifecycleCoordinator';
import type {
	SketchEntityReferenceTopologyOwnerRebuildInput,
} from '../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildCoordinator';
import { SketchEntityReferenceCreateCommitCoordinator } from './command/create/SketchEntityReferenceCreateCommitCoordinator';
import { SketchEntityReferenceLifecycleCoordinator } from './lifecycle/SketchEntityReferenceLifecycleCoordinator';
import { SketchEntityReferencePreviewCoordinator } from './command/preview/SketchEntityReferencePreviewCoordinator';
import { SketchEntityReferenceRebuildCoordinator } from './rebuild/SketchEntityReferenceRebuildCoordinator';
import { SketchEntityReferenceRebuildServiceFactory } from './rebuild/SketchEntityReferenceRebuildServiceFactory';
import { createExecutionDiagnostic } from './model/SketchEntityReferenceDiagnostics';
import type {
	CreateSketchEntityReferencesResult,
	RebuildSketchEntityReferencesResult,
} from './model/SketchEntityReferenceResults';
import type { CreateGeometryReferencesInput } from './model/SketchEntityReferenceTypes';
import { CreateGeometryReferencesCommandService } from './command/create/CreateGeometryReferencesCommandService';
import { InvalidateSketchEntityReferencesService } from './lifecycle/InvalidateSketchEntityReferencesService';
import { SketchEntityReferencePreviewRetirementService } from './command/preview/SketchEntityReferencePreviewRetirementService';
import { SketchEntityReferencePreviewService } from './command/preview/SketchEntityReferencePreviewService';
import { SketchEntityReferenceProjectionService } from './source/projection/SketchEntityReferenceProjectionService';
import { RemoveGeometryReferencesService } from './lifecycle/RemoveGeometryReferencesService';
import { SketchEntityReferenceObjectCleanupService } from './target/SketchEntityReferenceObjectCleanupService';
import { SketchReferenceRelationAttachDispatcher } from './relation/SketchReferenceRelationAttachDispatcher';
import {
	SketchEntityReferenceHistoryMutationService,
	type SketchEntityReferenceHistoryDeleteInput,
	type SketchEntityReferenceHistoryRestoreInput,
} from './history/SketchEntityReferenceHistoryMutationService';
import type { RemoveGeometryReferencesResult } from '../../../types/reference/sketch-entity-reference/RemoveGeometryReferencesService';
import type {
	TimelineReferenceCheckpointInput,
	TimelineReferenceCheckpointResult,
	TimelineReferenceSnapshot,
} from '../../../types/feature/FeatureTimelineRebuild';
import { SketchEntityReferenceSelectionCandidateResolver } from './command/selection/SketchEntityReferenceSelectionCandidateResolver';
import { TopologyFaceBoundarySelectionService } from './command/selection/TopologyFaceBoundarySelectionService';
import { TopologyFaceBoundaryRuntimeService } from './source/topology/TopologyFaceBoundaryRuntimeService';
import type {
	SketchEntityReferenceSelectionCandidate,
} from './command/selection/SketchEntityReferenceSelectionAdapter';
import {
	SketchEntityReferenceMoveDetachService,
} from './lifecycle/SketchEntityReferenceMoveDetachService';
import type {
	SketchEntityReferenceMoveDetachContext,
	SketchEntityReferenceMoveDetachInput,
	SketchEntityReferenceMoveDetachPort,
} from '../../../types/reference/sketch-entity-reference/SketchEntityReferenceMoveDetachTypes';

export type {
	SketchEntityReferenceControllerHost,
	SketchEntityReferenceControllerOptions,
	SketchEntityReferencePreviewSession,
	SketchEntityReferenceSelectionResolveInput,
	SketchEntityReferenceAllInnerLoopsResolveInput,
} from '../../../types/reference/sketch-entity-reference/SketchEntityReferenceController';

/**
 * 当前 Viewer 文档的转换实体引用控制器。
 *
 * 设计说明：
 * 1. Controller 负责装配文档级服务、协调器与订阅生命周期；
 * 2. 文档级对象在当前 Controller 生命周期内复用；
 * 3. preview coordinator 含命令级状态，每次命令必须创建独立实例；
 * 4. 对外只暴露创建、生命周期、重建和历史补丁等任务级入口，不泄漏内部 Service 或 Repository。
 */
export class SketchEntityReferenceController implements SketchEntityReferenceMoveDetachPort {
	/** 统一装配正式场景与历史 checkpoint 引用重建服务的工厂。 */
	private readonly rebuildServiceFactory = new SketchEntityReferenceRebuildServiceFactory();
	/** Preview 几何求值与实体构建的无状态应用服务。 */
	private readonly previewService: SketchEntityReferencePreviewService;
	/** Preview 场景对象与自有 geometry 的统一退役服务。 */
	private readonly previewRetirementService: SketchEntityReferencePreviewRetirementService;
	/** 当前文档复用的创建提交协调器。 */
	private readonly createCommitCoordinator!: SketchEntityReferenceCreateCommitCoordinator;
	/** 当前文档复用的生命周期协调器；统一路由目标删除、来源失效和手动解除。 */
	private readonly lifecycleCoordinator!: SketchEntityReferenceLifecycleCoordinator;
	/** 当前文档复用的统一重建协调器；自动监听由 ensureRuntimeActive 按需激活。 */
	private readonly rebuildCoordinator!: SketchEntityReferenceRebuildCoordinator;
	/** 当前文档同步应用 Record 与目标 tag 的历史变更服务。 */
	private readonly historyMutationService: SketchEntityReferenceHistoryMutationService;
	/** 移动转换实体前同步解除引用记录、关系与目标标记的领域服务。 */
	private readonly moveDetachService: SketchEntityReferenceMoveDetachService;
	/** Convert Entities topology 单边、面外环与 inner Wire 候选解析服务。 */
	private readonly boundarySelectionService: TopologyFaceBoundarySelectionService;
	/** 文档级自动监听是否已经激活。 */
	private runtimeActive = false;
	/** 防止重复释放和销毁后继续使用。 */
	private disposed = false;

	/**
	 * 创建当前 Viewer 文档的转换实体引用运行时。
	 *
	 * @param options 当前文档唯一 Repository 与 Viewer 最小宿主能力。
	 */
	public constructor(private readonly options: SketchEntityReferenceControllerOptions) {
		const { repository, host } = options;
		const objectCleanupService = new SketchEntityReferenceObjectCleanupService();
		const projectionService = new SketchEntityReferenceProjectionService({
			runtime: host,
		});
		const baseSelectionResolver = new SketchEntityReferenceSelectionCandidateResolver();
		const boundaryRuntime = new TopologyFaceBoundaryRuntimeService({
			resolveTopologyOwnerHost: descriptor => (
				host.resolveTopologyOwnerHost(descriptor)
			),
			...(host.resolveExactTopologyOwnerHost
				? {
					resolveExactTopologyOwnerHost: descriptor => (
						host.resolveExactTopologyOwnerHost!(descriptor)
					),
				}
				: {}),
			queryTopologyFaceBoundaries: (payload) => {
				if (!host.queryTopologyFaceBoundaries) {
					throw new Error('当前 Viewer 未装配 Convert Entities Face/Wire 查询能力');
				}
				return host.queryTopologyFaceBoundaries(payload);
			},
			queryTopologyInnerLoopByEdge: (payload) => {
				if (!host.queryTopologyInnerLoopByEdge) {
					throw new Error('当前 Viewer 未装配 Convert Entities 按边查询 InnerWire 能力');
				}
				return host.queryTopologyInnerLoopByEdge(payload);
			},
		});
		this.boundarySelectionService = new TopologyFaceBoundarySelectionService({
			resolveBaseCandidate: (targetObject, targetSketchId, selectionOptions) => (
				baseSelectionResolver.resolve({
					targetObject,
					targetSketchId,
					options: selectionOptions,
				})
			),
			boundaryRuntime,
		});

		this.previewService = new SketchEntityReferencePreviewService({
			runtime: host,
			projectionService,
		});
		this.previewRetirementService = new SketchEntityReferencePreviewRetirementService();
		this.historyMutationService = new SketchEntityReferenceHistoryMutationService({
			repository,
			runtime: host,
		});

		const relationAttachDispatcher = new SketchReferenceRelationAttachDispatcher();
		this.moveDetachService = new SketchEntityReferenceMoveDetachService(
			repository,
			(reference, targetEntity) => {
				relationAttachDispatcher.detach({
					reference,
					targetEntity,
				});
			},
			reference => host.getSketchObject(
				reference.targetSketchId,
				reference.targetEntityId,
			),
		);
		const removeService = new RemoveGeometryReferencesService({
			repository,
			relationAttachDispatcher,
			objectCleanupService,
			runtime: host,
		});

		const createCommandService = new CreateGeometryReferencesCommandService({
			runtime: host,
			repository,
			relationAttachDispatcher,
			objectCleanupService,
			projectionService,
			solveTargetSketchParametricState: targetSketchId => (
				host.solveTargetSketchParametricState(targetSketchId)
			),
		});

		this.createCommitCoordinator = new SketchEntityReferenceCreateCommitCoordinator({
			executeBatch: input => createCommandService.execute(input),
			rollbackCreatedReferences: referenceIds => removeService.removeReferencesWithTargets(referenceIds),
			getSketchGroup: sketchId => host.getSketchGroup(sketchId),
			getSketchObject: (sketchId, entityId) => host.getSketchObject(sketchId, entityId),
			shouldSyncShadedSketchContours: () => host.shouldSyncShadedSketchContours(),
		});

		const rebuildService = this.rebuildServiceFactory.create({
			runtime: host,
			repository,
			relationAttachDispatcher,
			objectCleanupService,
			projectionService,
			createService: createCommandService,
			removeService,
			solveTargetSketchParametricState: targetSketchId => (
				host.solveTargetSketchParametricState(targetSketchId)
			),
		});

		this.rebuildCoordinator = new SketchEntityReferenceRebuildCoordinator({
			getDefinitionChangeHub: () => host.getDefinitionChangeHub(),
			observeAssemblyTransformCommitted: () => host.observeAssemblyTransformCommitted(),
			resolveTopologyOwnersByAssemblyTransform: record => host.resolveTopologyOwnersByAssemblyTransform(record),
			rebuildService,
		});

		const invalidationService = new InvalidateSketchEntityReferencesService({
			repository,
			relationAttachDispatcher,
			runtime: host,
		});

		this.lifecycleCoordinator = new SketchEntityReferenceLifecycleCoordinator({
			observeSketchEntitiesRemoved: () => host.observeSketchEntitiesRemoved(),
			observeSourceSketchRemoved: () => host.observeSourceSketchRemoved(),
			observeTopologyOwnerRemoved: () => host.observeTopologyOwnerRemoved(),
			repository,
			invalidationService,
			removeService,
			reportAutomaticResult: result => host.reportReferenceLifecycleResult(result),
		});
	}

	/**
	 * 移动转换实体前同步解除其外部引用。
	 *
	 * 该入口与创建、重建和生命周期服务共享同一文档级仓库及关系分发器，
	 * 确保移动历史补丁、引用记录和运行时约束始终处于同一所有权边界。
	 *
	 * @param input 当前移动命令选中的草图实体。
	 * @returns 可随移动命令提交或回滚的引用解除上下文。
	 */
	public detach(input: SketchEntityReferenceMoveDetachInput): SketchEntityReferenceMoveDetachContext {
		this.assertActive();
		return this.moveDetachService.detach(input);
	}

	/**
	 * 解析一次 Convert Entities 面、环、边或草图来源选择。
	 *
	 * @param input 命中对象、目标草图和命令选择模式。
	 * @returns 可直接交给 UIBridge 的候选。
	 */
	public resolveSelectionCandidate(
		input: SketchEntityReferenceSelectionResolveInput,
	): Promise<SketchEntityReferenceSelectionCandidate | null> {
		this.assertActive();
		return this.boundarySelectionService.resolve(input);
	}

	/**
	 * 解析当前全部已选 Face 的 inner Wire 候选。
	 *
	 * @param input 当前已选 Face。
	 * @returns 每条 inner Wire 对应的一条 loop 候选。
	 */
	public resolveAllInnerLoopCandidates(
		input: SketchEntityReferenceAllInnerLoopsResolveInput,
	): Promise<readonly SketchEntityReferenceSelectionCandidate[]> {
		this.assertActive();
		return this.boundarySelectionService.resolveAllInnerLoops(
			input.faceSources,
		);
	}

	/**
	 * 确保当前文档的自动重建与删除监听已经建立。
	 *
	 * 该入口仅在文档创建或恢复引用后调用；重复调用不得重复订阅。
	 */
	public ensureRuntimeActive(): void {
		this.assertActive();
		if (this.runtimeActive) {
			return;
		}
		this.rebuildCoordinator.activate();
		this.lifecycleCoordinator.activate();
		this.runtimeActive = true;
	}

	/**
	 * 创建一次 Convert Entities 命令独占的 Preview 会话。
	 *
	 * @returns 能力收窄且可独立释放的命令级 Preview 会话。
	 */
	public createPreviewSession(): SketchEntityReferencePreviewSession {
		this.assertActive();
		return new SketchEntityReferencePreviewCoordinator({
			buildPreviewBatch: input => this.previewService.buildPreviewBatch(input),
			retirePreviewObjects: previewObjects => this.previewRetirementService.retirePreviewObjects(previewObjects),
		});
	}

	/**
	 * 提交一次转换实体引用创建请求。
	 *
	 * @param input 创建输入。
	 * @returns 创建提交结果。
	 */
	public commitReferences(
		input: CreateGeometryReferencesInput,
	): Promise<CreateSketchEntityReferencesResult> {
		this.assertActive();
		return this.createCommitCoordinator.commit(input);
	}

	/**
	 * 同步恢复历史补丁中的引用记录与目标 tag。
	 *
	 * @param input 恢复记录与应用前记录。
	 */
	public restoreReferenceFromHistorySync(
		input: SketchEntityReferenceHistoryRestoreInput,
	): void {
		this.assertActive();
		this.historyMutationService.restoreReferenceSync(input);
	}

	/**
	 * 同步删除历史补丁中的引用记录与目标 tag。
	 *
	 * @param input 删除 ID 与应用前记录。
	 */
	public deleteReferenceFromHistorySync(
		input: SketchEntityReferenceHistoryDeleteInput,
	): void {
		this.assertActive();
		this.historyMutationService.deleteReferenceSync(input);
	}

	/**
	 * 在异步前向求值开始前，同步冻结本轮目标草图需要处理的引用集合。
	 *
	 * 这里必须同步读取并深拷贝，避免 Worker 求值期间新增、删除或修改引用，
	 * 导致本轮 checkpoint 消费的集合发生漂移。
	 *
	 * @param targetSketchIds 本轮时间线会到达的目标草图 ID。
	 * @returns 与后续异步求值隔离的引用快照。
	 */
	public captureTimelineRebuildReferences(
		targetSketchIds: readonly string[],
	): TimelineReferenceSnapshot {
		this.assertActive();
		const references = this.options.repository.captureReferencesByTargetSketchIds(
			targetSketchIds,
		);

		return {
			items: references.map((reference) => {
				const capturedReference = structuredClone(reference);
				return {
					referenceId: capturedReference.referenceId,
					targetSketchId: capturedReference.targetSketchId,
					sourceDescriptor: structuredClone(capturedReference.sourceDescriptor),
					reference: capturedReference,
				};
			}),
		};
	}

	/**
	 * 在时间线到达目标草图时，使用该位置的 checkpoint runtime 更新正式草图。
	 *
	 * 本入口只消费执行开始时冻结的引用，不回查当前仓库来扩大处理范围；
	 * checkpoint 找不到来源时由重建服务保留目标实体并写入 dangling 警告。
	 *
	 * @param input 冻结快照、当前目标草图与只读 checkpoint runtime。
	 * @returns 本次正式草图引用更新结果。
	 */
	public rebuildTimelineCheckpoint(
		input: TimelineReferenceCheckpointInput,
	): Promise<TimelineReferenceCheckpointResult> {
		this.assertActive();
		const targetSketchIdSet = new Set(input.targetSketchIds);
		const references = input.snapshot.items
			.filter((item) => targetSketchIdSet.has(item.targetSketchId))
			.map((item) => structuredClone(item.reference));
		const checkpointService = this.rebuildServiceFactory.create({
			runtime: input.runtime,
			repository: this.options.repository,
			solveTargetSketchParametricState: targetSketchId => (
				this.options.host.solveTargetSketchParametricState(targetSketchId)
			),
		});

		return this.rebuildCoordinator.runWithAutomaticPropagationSuppressed(
			input.propagationId,
			() => checkpointService.rebuildTimelineCheckpointReferences(
				references,
				input.propagationId,
				input.isInputCurrent,
			),
		);
	}

	/**
	 * 回滚已经创建完成、但未能写入历史事务的引用集合。
	 *
	 * @param referenceIds 已落地的引用记录 ID。
	 * @returns 统一 Removal 结果。
	 */
	public rollbackCreatedReferences(
		referenceIds: readonly string[],
	): Promise<RemoveGeometryReferencesResult> {
		this.assertActive();
		return this.createCommitCoordinator.rollbackCreatedReferences(referenceIds);
	}

	/**
	 * 删除目标草图实体上的指定引用关系。
	 *
	 * @param input 引用关系删除输入。
	 * @returns 引用关系删除结果。
	 */
	public removeRelation(
		input: SketchEntityReferenceRemoveRelationInput,
	): Promise<SketchEntityReferenceRemoveRelationResult> {
		this.assertActive();
		return this.lifecycleCoordinator.removeRelation(input);
	}

	/**
	 * 按当前 topology owner 显式触发引用重建。
	 *
	 * @param input topology owner 级重建输入。
	 * @returns topology owner 级引用重建结果。
	 */
	public rebuildTopologyOwners(
		input: SketchEntityReferenceTopologyOwnerRebuildInput,
	): Promise<RebuildSketchEntityReferencesResult> {
		this.assertActive();
		if (!this.runtimeActive) {
			return Promise.resolve({
				keptReferenceIds: [],
				removedReferenceIds: [],
				failedReferenceIds: [],
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'rebuild-failed',
						level: 'warning',
						message: '转换实体引用运行时尚未激活，跳过 topology owner 重建',
					}),
				],
			});
		}
		return this.rebuildCoordinator.rebuildTopologyEdgeReferencesForCurrentOwners(input);
	}

	/**
	 * 释放当前文档持有的全部引用监听与运行时资源。
	 */
	public dispose(): void {
		if (this.disposed) {
			return;
		}
		this.lifecycleCoordinator.dispose();
		this.rebuildCoordinator.dispose();
		this.runtimeActive = false;
		this.disposed = true;
	}

	/**
	 * 阻止已经销毁的 Controller 继续创建或读取业务对象。
	 */
	private assertActive(): void {
		if (this.disposed) {
			throw new Error('转换实体引用 Controller 已释放，不能继续使用');
		}
	}
}
